/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AviAlertBellModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';

import {
    SignpostPosition,
    signpostPositionMap,
    SIGNPOST_BOTTOM_SHIFTED_RIGHT_CONNECTED_POSITION,
    SIGNPOST_TOP_SHIFTED_RIGHT_CONNECTED_POSITION,
} from 'ng/modules/tooltip/components/signpost/signpost.constants';

import { ConnectedPosition } from '@angular/cdk/overlay';
import { L10nService } from '@vmw/ngx-vip';
import { Subject } from 'rxjs';

import * as globalL10n from 'global-l10n';
import * as l10n from './avi-alert-bell.l10n';

import {
    IAlert,
    IAlertLevel,
    IAlertsRuntime,
} from '../../avi-alert-bell.types';

import './avi-alert-bell.component.less';

/**
 * Alert bell views based on pool state.
 */
enum AlertBellView {
    LOADING = 'loading',
    ERROR = 'error',
}

const ALERT_LIST = [
    'high',
    'medium',
    'low',
];

/**
 * Selectors for Alert bell wrappers. If hovered element is not a Alert bell wrapper,
 * then close the signpost.
 */
const ALERT_BELL_WRAPPERS = 'signpost';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description Avi Alert bell component.
 * @author Umesh Chovatiya
 */
@Component({
    selector: 'avi-alert-bell',
    templateUrl: './avi-alert-bell.component.html',
})
export class AviAlertBellComponent implements OnChanges, OnDestroy {
    /**
     * Runtime data for the given VS / Pool.
     * This data is used to render the Alert bell.
     */
    @Input()
    public runTime: IAlertsRuntime;

    @Input()
    public limit: number;

    @Input()
    public step: number;

    @Input()
    public hideViewAlertButton = false;

    @Output()
    public onViewAlertsClick = new EventEmitter<void>();

    @Output()
    public onAlertBellIconClick = new EventEmitter<void>();

    @Output()
    public objectName?: string;

    public signpostPosition: SignpostPosition;

    public readonly signpostPositionPriorities: ConnectedPosition[] = [
        SIGNPOST_BOTTOM_SHIFTED_RIGHT_CONNECTED_POSITION,
        SIGNPOST_TOP_SHIFTED_RIGHT_CONNECTED_POSITION,
    ];

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly alertList = ALERT_LIST;

    /**
     * Hold the Alert bell class name based on score.
     */
    public alertBellClass: string;

    public title: string;

    public uuid: string;

    public alerts: IAlert;

    /**
     * Subject to control the hide/show signpost close button.
     */
    public readonly signpostCloseControl$ = new Subject<boolean>();

    public readonly alertBellView = AlertBellView;

    /**
     * Current view to render based on state of Alert bell.
     */
    public currentViewToRender = AlertBellView.LOADING;

    /**
     * To hide the signpost for the first time.
     */
    private showSignpostForFirstTime = true;

    constructor(
        public readonly l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    public get totalAlerts(): number {
        const { high, low, medium } = this.alerts;

        return high + low + medium;
    }

    public alertClass(alert: string): string {
        return `avi-alert-bell__colors--${alert}`;
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { runTime } = changes;

        if (runTime && runTime.currentValue) {
            this.fetchAlertsData();
        }
    }

    public onAlertUpdate(alerts: IAlertLevel): void {
        this.alerts = {
            high: alerts.ALERT_HIGH,
            low: alerts.ALERT_LOW,
            medium: alerts.ALERT_MEDIUM,
        };
    }

    /**
     * Fetch the Alert bell related data for current item.
     */
    public handleAlertBellClick(): void {
        this.onAlertBellIconClick.emit();
    }

    /**
     * Fetch the Alert bell related data for current item.
     */
    public fetchAlertsData(): void {
        this.currentViewToRender = AlertBellView.LOADING;

        const { config, alert } = this.runTime;

        this.alerts = alert;
        this.title = config.name;
        this.uuid = config.uuid;

        if (this.showSignpostForFirstTime) {
            this.signpostCloseControl$.next(true);
            this.showSignpostForFirstTime = false;
        } else {
            this.onAlertBellIconClick.emit();
        }
    }

    public closeSignpost(): void {
        this.signpostCloseControl$.next(false);
    }

    /**
     * Handle click event on the view all alerts button.
     */
    public handleViewAllAlertsButtonClick(): void {
        this.onViewAlertsClick.emit();
    }

    /**
     * Notify signpost to update position when the connected position from avi-tooltip changes.
     */
    public handleSignpostConnectedPosition(signPostConnectedPosition: ConnectedPosition): void {
        this.signpostPosition = signpostPositionMap.get(signPostConnectedPosition);
    }

    /**
     * Handle mouse leave event on the Alert bell count and tooltip.
     */
    public handleAlertBellMouseLeave($event: MouseEvent): void {
        // Get the current mouse over element
        const { toElement: hoverElement } = $event as any;

        if (!hoverElement?.className?.includes(ALERT_BELL_WRAPPERS)) {
            this.closeSignpost();
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.closeSignpost();
    }
}
